import React, { forwardRef } from 'react';
import Type from 'prop-types';
import { noop } from 'lodash';
import cn from 'classnames';
import { Divider, InputBase, InputAdornment, IconButton } from "@mui/material";
import { Clear as ClearIcon, ArrowBack as ArrowBackIcon } from "@mui/icons-material";
import { useTranslation } from 'hooks';
import { t as translateFn } from 'utils/i18n';
import useSearchBar from './useSearchBar';
import styles from './SearchInput.module.css';

const SearchInput = forwardRef(function SearchInput(props, ref) {
  const {
    value,
    inputRef,
    onChange,
    onClear,
    onSearch: handleSearch,
  } = useSearchBar(props);
  const {
    value: inputValue,
    fullWidth,
    visible,
    focusOnOpen,
    icon: Icon,
    onIconClick,
    InputProps,
    className,
    showDivider,
    disableGutters,
    timeoutMs,
    onClear: onInputClear,
    onChange: onInputChange,
    onSearch: onInputSearch,
    ...other
  } = props;
  const { t } = useTranslation();

  const renderStartAdornment = () => {
    return (
      Icon && (
        <InputAdornment position="start">
          <IconButton edge="start" onClick={onIconClick}>
            <Icon />
          </IconButton>
        </InputAdornment>
      )
    );
  };

  const renderEndAdornment = () => {
    return (
      value && (
        <InputAdornment position="end">
          <IconButton edge="end" onClick={onClear}>
            <ClearIcon />
          </IconButton>
        </InputAdornment>
      )
    );
  };

  return (
    <>
      <div
        ref={ref}
        className={cn(className, {
          [styles.fullWidth]: fullWidth,
          [styles.gutters]: !disableGutters,
        })}
        role="search"
        autoComplete="off"
        style={{
          visibility: visible ? 'visible' : 'hidden',
        }}
      >
        <InputBase
          type="search"
          value={value}
          autoComplete="off"
          onChange={onChange}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              handleSearch(event);
              if (inputRef.current) {
                inputRef.current.blur();
              }
            }
          }}
          inputRef={inputRef}
          fullWidth={fullWidth}
          startAdornment={renderStartAdornment()}
          endAdornment={renderEndAdornment()}
          inputProps={{
            ...InputProps,
            role: 'combobox',
            'aria-label': t('search'),
            'aria-haspopup': 'false',
          }}
          {...other}
        />
      </div>
      {showDivider && <Divider />}
    </>
  );
});

SearchInput.propTypes = {
  placeholder: Type.string,
  fullWidth: Type.bool,
  showDivider: Type.bool,
  visible: Type.bool,
  focusOnOpen: Type.bool,
  disableGutters: Type.bool,
  className: Type.string,
  timeoutMs: Type.number,
  icon: Type.elementType,
  InputProps: Type.object, // eslint-disable-line react/forbid-prop-types
  value: Type.string,
  onChange: Type.func,
  onClear: Type.func,
  onSearch: Type.func,
  onIconClick: Type.func,
};

SearchInput.defaultProps = {
  value: '',
  placeholder: translateFn('search'),
  fullWidth: false,
  showDivider: false,
  disableGutters: false,
  visible: false,
  focusOnOpen: true,
  timeoutMs: 1500,
  className: null,
  InputProps: {},
  icon: ArrowBackIcon,
  onChange: noop,
  onClear: noop,
  onIconClick: noop,
  onSearch: noop,
};

export default SearchInput;
